import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";

type LightboxImage = {
  id?: string | null;
  url: string;
  altText?: string | null;
};

type UseProductImageLightboxProps = {
  images: Array<LightboxImage>;
  selectedIndex: number;
  onSelect: (index: number) => void;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export function useProductImageLightbox({
  images,
  selectedIndex,
  onSelect,
}: Readonly<UseProductImageLightboxProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const pinchDistanceRef = useRef<number | null>(null);
  const panAnimationFrameRef = useRef<number | null>(null);
  const pendingPanRef = useRef<{ x: number; y: number } | null>(null);
  const didPanRef = useRef(false);

  const flushPendingPan = useCallback(() => {
    if (!pendingPanRef.current) return;
    setPan(pendingPanRef.current);
    pendingPanRef.current = null;
    panAnimationFrameRef.current = null;
  }, []);

  const queuePanUpdate = useCallback(
    (nextPan: { x: number; y: number }) => {
      pendingPanRef.current = nextPan;
      if (panAnimationFrameRef.current !== null) return;

      panAnimationFrameRef.current = requestAnimationFrame(flushPendingPan);
    },
    [flushPendingPan]
  );

  const cancelPanAnimationFrame = useCallback(() => {
    if (panAnimationFrameRef.current === null) return;
    cancelAnimationFrame(panAnimationFrameRef.current);
    panAnimationFrameRef.current = null;
    pendingPanRef.current = null;
  }, []);

  const clampZoom = useCallback((nextZoom: number) => {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
  }, []);

  const resetTransform = useCallback(() => {
    cancelPanAnimationFrame();
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    pointerStartRef.current = null;
    panStartRef.current = null;
    pinchDistanceRef.current = null;
  }, [cancelPanAnimationFrame]);

  const updateZoom = useCallback(
    (nextZoom: number) => {
      const clampedZoom = clampZoom(nextZoom);
      setZoomLevel(clampedZoom);

      if (clampedZoom <= 1) {
        setPan({ x: 0, y: 0 });
        setIsPanning(false);
      }
    },
    [clampZoom]
  );

  const open = useCallback(
    (index: number) => {
      onSelect(index);
      resetTransform();
      setIsOpen(true);
    },
    [onSelect, resetTransform]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    resetTransform();
  }, [resetTransform]);

  const goToPrevious = useCallback(() => {
    if (!images.length) return;
    const previousIndex =
      (selectedIndex - 1 + images.length) % Math.max(images.length, 1);
    onSelect(previousIndex);
    resetTransform();
  }, [images.length, onSelect, resetTransform, selectedIndex]);

  const goToNext = useCallback(() => {
    if (!images.length) return;
    const nextIndex = (selectedIndex + 1) % Math.max(images.length, 1);
    onSelect(nextIndex);
    resetTransform();
  }, [images.length, onSelect, resetTransform, selectedIndex]);

  const zoomIn = useCallback(() => {
    updateZoom(zoomLevel + 0.25);
  }, [updateZoom, zoomLevel]);

  const zoomOut = useCallback(() => {
    updateZoom(zoomLevel - 0.25);
  }, [updateZoom, zoomLevel]);

  const startPan = useCallback(
    (clientX: number, clientY: number) => {
      if (zoomLevel <= 1) return;

      setIsPanning(true);
      didPanRef.current = false;
      pointerStartRef.current = { x: clientX, y: clientY };
      panStartRef.current = pan;
    },
    [pan, zoomLevel]
  );

  const movePan = useCallback(
    (clientX: number, clientY: number) => {
      if (
        !isPanning ||
        !pointerStartRef.current ||
        !panStartRef.current ||
        zoomLevel <= 1
      ) {
        return;
      }

      const deltaX = clientX - pointerStartRef.current.x;
      const deltaY = clientY - pointerStartRef.current.y;

      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        didPanRef.current = true;
      }

      queuePanUpdate({
        x: panStartRef.current.x + deltaX,
        y: panStartRef.current.y + deltaY,
      });
    },
    [isPanning, queuePanUpdate, zoomLevel]
  );

  const stopPan = useCallback(() => {
    flushPendingPan();
    setIsPanning(false);
    pointerStartRef.current = null;
    panStartRef.current = null;
  }, [flushPendingPan]);

  const getTouchDistance = useCallback((touchList: React.TouchList) => {
    if (touchList.length < 2) return null;

    const firstTouch = touchList[0];
    const secondTouch = touchList[1];
    const horizontal = secondTouch.clientX - firstTouch.clientX;
    const vertical = secondTouch.clientY - firstTouch.clientY;

    return Math.hypot(horizontal, vertical);
  }, []);

  const onWheel = useCallback(
    (event: React.WheelEvent<HTMLElement>) => {
      event.preventDefault();
      const zoomDelta = event.deltaY < 0 ? 0.2 : -0.2;
      updateZoom(zoomLevel + zoomDelta);
    },
    [updateZoom, zoomLevel]
  );

  const onMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      startPan(event.clientX, event.clientY);
    },
    [startPan]
  );

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      movePan(event.clientX, event.clientY);
    },
    [movePan]
  );

  const onTouchStart = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length >= 2) {
        pinchDistanceRef.current = getTouchDistance(event.touches);
        stopPan();
        return;
      }

      const touch = event.touches[0];
      startPan(touch.clientX, touch.clientY);
    },
    [getTouchDistance, startPan, stopPan]
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length >= 2) {
        event.preventDefault();
        const nextDistance = getTouchDistance(event.touches);
        const previousDistance = pinchDistanceRef.current;
        if (!nextDistance || !previousDistance) return;

        const ratio = nextDistance / previousDistance;
        updateZoom(zoomLevel * ratio);
        pinchDistanceRef.current = nextDistance;
        return;
      }

      const touch = event.touches[0];
      event.preventDefault();
      movePan(touch.clientX, touch.clientY);
    },
    [getTouchDistance, movePan, updateZoom, zoomLevel]
  );

  const onTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length === 0) {
        stopPan();
        pinchDistanceRef.current = null;
      }
    },
    [stopPan]
  );

  const onImageClick = useCallback(() => {
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }

    if (zoomLevel > 1) {
      resetTransform();
      return;
    }

    updateZoom(2);
  }, [resetTransform, updateZoom, zoomLevel]);

  useEffect(() => {
    return () => {
      cancelPanAnimationFrame();
    };
  }, [cancelPanAnimationFrame]);

  return {
    isOpen,
    zoomLevel,
    pan,
    isPanning,
    open,
    close,
    resetTransform,
    goToPrevious,
    goToNext,
    zoomIn,
    zoomOut,
    onWheel,
    onMouseDown,
    onMouseMove,
    onMouseUp: stopPan,
    onMouseLeave: stopPan,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onImageClick,
  };
}
